/**
 * Ticket 7 — Manager Hub: Derailment Watch panel
 *
 * Drop into client/src/components/manager/DerailmentWatchPanel.tsx and
 * mount inside pages/Manager.tsx (the Manager Hub surface).
 *
 * Lists recent nudges with their ack status. Manager can see all nudges on
 * their tenant. AE can see their own from the AE view (separate mount).
 */

import { useEffect, useState } from "react";
import { api } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";

interface DerailmentNudge {
  id: string;
  account_id: number;
  account_name?: string;
  cp_number: number;
  ae_user_id: string;
  ae_name?: string;
  channel: string;
  status: "sent" | "failed" | "acknowledged";
  sent_at: string;
  ack_at: string | null;
  failure_reason: string | null;
}

export function DerailmentWatchPanel() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<DerailmentNudge[]>([]);

  async function load() {
    setLoading(true);
    try {
      const j = await api<{ nudges?: DerailmentNudge[] }>("/api/derailment-nudges?days=30");
      setRows(j?.nudges ?? []);
    } catch (e: any) {
      const detail = e instanceof Error && e.message ? ` · ${e.message}` : "";
      toast({ title: "Could not load derailment nudges", description: `Please refresh${detail}`, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function ack(id: string) {
    try {
      await api(`/api/derailment-nudges/${id}/ack`, { method: "POST" });
      setRows((prev) => prev.map((r) => r.id === id ? { ...r, status: "acknowledged", ack_at: new Date().toISOString() } : r));
      toast({ title: "Acknowledged" });
    } catch (e: any) {
      const detail = e instanceof Error && e.message ? ` · ${e.message}` : "";
      toast({ title: "Ack failed", description: `Please try again${detail}`, variant: "destructive" });
    }
  }

  if (loading) {
    return <div data-testid="derailment-loading" style={{ padding: 16, color: "#525258" }}>Loading derailment watch…</div>;
  }
  if (rows.length === 0) {
    return (
      <div data-testid="derailment-empty" style={{ padding: 16, color: "#525258" }}>
        No derailment nudges in the last 30 days. Either every account is fresh, or the cron has not run yet.
      </div>
    );
  }

  return (
    <section data-testid="derailment-watch-panel" style={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: 8, padding: 12 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: "#0B0B0F", margin: 0 }}>Derailment Watch</h2>
        <span style={{ fontSize: 11, color: "#8a8a93" }}>{rows.length} nudges, last 30 days</span>
      </header>
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid rgba(15,23,42,0.1)", textAlign: "left" }}>
            <th style={{ padding: "6px 4px", fontWeight: 600 }}>Account</th>
            <th style={{ padding: "6px 4px", fontWeight: 600 }}>CP</th>
            <th style={{ padding: "6px 4px", fontWeight: 600 }}>AE</th>
            <th style={{ padding: "6px 4px", fontWeight: 600 }}>Sent</th>
            <th style={{ padding: "6px 4px", fontWeight: 600 }}>Status</th>
            <th style={{ padding: "6px 4px", fontWeight: 600 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`derailment-row-${r.id}`} style={{ borderBottom: "1px solid rgba(15,23,42,0.05)" }}>
              <td style={{ padding: "6px 4px" }}>{r.account_name ?? `#${r.account_id}`}</td>
              <td style={{ padding: "6px 4px" }}>CP{r.cp_number}</td>
              <td style={{ padding: "6px 4px" }}>{r.ae_name ?? r.ae_user_id.slice(0, 8)}</td>
              <td style={{ padding: "6px 4px" }}>{new Date(r.sent_at).toLocaleDateString()}</td>
              <td style={{ padding: "6px 4px", color: r.status === "failed" ? "#DC2626" : r.status === "acknowledged" ? "#5a7a52" : "#0B0B0F" }}>
                {r.status}{r.status === "failed" && r.failure_reason ? ` — ${r.failure_reason}` : ""}
              </td>
              <td style={{ padding: "6px 4px" }}>
                {r.status !== "acknowledged" ? (
                  <button
                    type="button"
                    onClick={() => void ack(r.id)}
                    data-testid={`derailment-ack-${r.id}`}
                    style={{ padding: "2px 8px", fontSize: 11, fontWeight: 600, borderRadius: 4, background: "#0B0B0F", color: "#FAFAF7", border: "none", cursor: "pointer" }}
                  >Ack</button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
